import { Component, OnDestroy, OnInit } from '@angular/core';
import { AuthService } from 'src/app/core/services/auth.service';
import { Subject, catchError, filter, of, switchMap, take, takeUntil } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import LoginPrompt from 'src/app/core/enums/login-prompt.enum';
import { ConsoleLogger } from 'src/app/core/shared/console-logger';
import { MessageCode, UserMessage } from '../../core/shared/error/user-message';
import { ErrorDisplayData } from 'src/app/core/models/error-display-data.model';
import { Application } from 'src/portaljs/constants/application';
import { AaspTemplateFamily } from 'src/app/core/enums/aasp-template-family.enum';
import { PortalStateService } from 'src/app/core/services/portal-state.service';
import { LoginAction } from 'src/app/core/models/login-action';
import { InitialStateService } from 'src/app/core/services/initial-state-service';
import { UserLoginModel } from 'src/app/core/models/user-login.model';
import {EnvironmentService} from '../../core/services/environment.service';
import { IframeService } from 'src/app/core/services/iframe.service';

@Component({
    selector: 'fss-portal-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject();
  private processedCode: string | null = null;

  constructor(private authService: AuthService, private pss: PortalStateService, private route: ActivatedRoute,
              private router: Router, private initStateService: InitialStateService, private envService: EnvironmentService, private iframeService: IframeService) {
    ConsoleLogger.debug('xxxx - LoginComponent[0].constructor(): called');
    const params = this.route.snapshot.queryParams;
    if (params['nav']) {
      ConsoleLogger.debug('xxxx LoginComponent[1] initialNavInfoForIframeFromBookmarkUrl: ' + params['app'] + ' , ' + params['nav']);
      this.iframeService.setInitialNavInfoForIframeFromBookmarkUrl({app: params['app'], nav: params['nav']});
    }
  }

  public ngOnDestroy() {
    ConsoleLogger.debug('xxxx - LoginComponent[2].ngOnDestroy(): called');
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  public ngOnInit(): void {
    ConsoleLogger.debug('xxxx - LoginComponent[3].ngOnInit(): called');
    this.route.queryParams
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((params) => {
        ConsoleLogger.debug('xxxx - LoginComponent[4].ngOnInit(): paramsChange: ' + JSON.stringify(params));

        const param_error = params['error'];
        const param_errorCode = params['errorCode'];
        const param_fss_reason = params['fss_reason'];
        const param_app = params['app'];
        const param_appId = params['appId'];
        const param_nav = params['nav'];
        const param_link = params['link'];
        const param_noIPAuth = params['noIPAuth']; //Indicates that the next login should not be authenticated by IP
        const param_kioskMode = params['k'];

        if(param_kioskMode) {
          ConsoleLogger.debug('xxxx - LoginComponent[4a].ngOnInit(): param_kioskMode: ' + param_kioskMode);
          this.envService.setKioskMode(param_kioskMode === '1' ? true : false);
        }

        let param_continuationState = params['continuationState'];
        ConsoleLogger.debug('xxxx - LoginComponent[5].ngOnInit(): param_continuationState original: ' + param_continuationState);
        const initialNavLoginFailed = param_continuationState?.indexOf('nav_failed_login') >= 0;
        param_continuationState = param_continuationState?.replace('nav_failed_login', '');
        ConsoleLogger.debug('xxxx - LoginComponent[6].ngOnInit(): param_continuationState updated: ' + param_continuationState + ', initialNavLoginFailed: ' + initialNavLoginFailed);

        const param_siteGuid = params['siteGuid'];
        this.authService.temporary_deep_link = undefined;
        let unauthenticatedAtDistrict = false;
        this.authService.noIPAuthNextLogin = param_noIPAuth === 'true';

        if (param_error) {
          this.authService.logFailInfo(undefined, {fn: 'loginError', error: param_error, errorCode: param_errorCode, fss_reason: param_fss_reason}).pipe(take(1), takeUntil(this.ngUnsubscribe)).subscribe();
          ConsoleLogger.debug('xxxx - LoginComponent[7].ngOnInit(): param_error branch: ' + JSON.stringify(param_error));
          this.authService.killUserObjAndShowErrorMsg(new ErrorDisplayData(UserMessage.loginIssue, param_error, MessageCode.ERROR_LOGIN_ISSUE));
        } else if (param_errorCode) {
          if(param_errorCode === 'CANCEL') {
            ConsoleLogger.debug('xxxx - LoginComponent[8].ngOnInit(): param_errorCode === CANCEL');
            if (param_fss_reason) {
              ConsoleLogger.debug('xxxx - LoginComponent[9].ngOnInit(): param_errorCode: CANCEL - with reason');
              // If the user chooses forgot_password or create_account, aasp will cancel the login. Since DBO
              // requires an authCode to display the frame, we need to login as a Guest before we can load DBO
              // with an authCode. A known limitation is that this will not work at sites w/o guest access.
              this.authService.login({
                loginPrompt: LoginPrompt.NONE,
                fullscreen: true,
                app: param_app,
                fss_reason: param_fss_reason
              }, 'CANCEL_withReason');
            } else {
              ConsoleLogger.debug('xxxx - LoginComponent[10].ngOnInit(): param_errorCode: CANCEL - without reason');
              this.authService.temporary_continuation_state = param_continuationState;
              if (!param_siteGuid) {
                ConsoleLogger.debug('xxxx - LoginComponent[11].ngOnInit(): no param_siteGuid. goto Portal');
                this.initStateService.loginComponentInitialized = true;
                return this.router.navigate(['portal']);
              } else {
                ConsoleLogger.debug('xxxx - LoginComponent[12].ngOnInit(): logging in with continuation state');
                this.authService.login({
                  loginPrompt: LoginPrompt.NONE,
                  fullscreen: true,
                  app: param_app,
                  continuationState: this.authService.temporary_continuation_state
                }, 'CANCEL_noReason');
              }
            }
          } else {
            ConsoleLogger.debug('xxxx - LoginComponent[13].ngOnInit(): param_errorCode !== CANCEL: ' + param_errorCode);
            this.authService.temporary_continuation_state = param_continuationState;

            this.iframeService.setInitialNavInfoForIframeFromBookmarkUrl({app: param_app, nav: AuthService.stripNavToken(this.authService.temporary_continuation_state)});
            const loginAction: LoginAction = {
              loginPrompt: LoginPrompt.LOGIN,
              fullscreen: true,
              siteGuid: param_siteGuid,
              app: param_app
            };

            if (this.authService.temporary_continuation_state) {
              ConsoleLogger.debug('xxxx - LoginComponent[14].ngOnInit(): temporary_continuation_state exists');
              loginAction.continuationState = AuthService.CONTINUATION_STATE_NAV_TOKEN + AuthService.stripNavToken(this.authService.temporary_continuation_state);
            }

            if (param_errorCode === 'GUESTNOTALLOWED') {
              ConsoleLogger.debug('xxxx - LoginComponent[15].ngOnInit(): param_errorCode === GUESTNOTALLOWED');
              this.authService.temporary_deep_link = param_link;
              loginAction.createAccount = false;
              loginAction.forgotPassword = false;
            }

            ConsoleLogger.debug('xxxx - LoginComponent[16].ngOnInit(): param_errorCode: ' + param_errorCode + ' - logout/login...');
            this.authService.logout('errorCode_' + param_errorCode)
              .pipe(
                take(1),
                catchError(() => of(undefined)),
                takeUntil(this.ngUnsubscribe)
              )
              .subscribe(() => {
                ConsoleLogger.debug('xxxx LoginComponent[17] - param_errorCode: ' + param_errorCode + ' - logged out. Logging in with: ' + JSON.stringify(loginAction));
                this.authService.login(loginAction, 'errorCode_' + param_errorCode);
              });
          }
        } else {
          ConsoleLogger.debug('xxxx - LoginComponent[18].ngOnInit(): no errors branch');
          let districtAppId = this.authService.getDistrictAppId();
          this.authService.temporary_deep_link = param_link;
          this.authService.temporary_continuation_state = param_continuationState;

          // handle change of district and/or site
          if (param_appId) {
            ConsoleLogger.debug('xxxx - LoginComponent[19].ngOnInit(): param_appId branch: param_appId: ' + param_appId +  ', districtAppId: ' + districtAppId);
            let logoutDebugParam;
            let resetUserLoginModel = false;

            if(param_appId !== districtAppId) {
              ConsoleLogger.debug('xxxx - LoginComponent[20].ngOnInit(): param_appId !== districtAppId');
              districtAppId = param_appId;
              this.authService.setDistrictAppId(districtAppId);
              this.authService.setSiteGuid(undefined);
              logoutDebugParam = 'distIdMismatch';
              resetUserLoginModel = true;
            }

            this.authService.setSiteGuidParam(param_siteGuid);
            if (param_siteGuid && param_siteGuid !== this.authService.getSiteGuid()) {
              ConsoleLogger.debug('xxxx - LoginComponent[21].ngOnInit(): param_siteGuid && param_siteGuid !== getSiteGuid: param_siteGuid: ' + param_siteGuid + ', siteGuid: ' + this.authService.getSiteGuid());
              this.authService.setSiteGuid(param_siteGuid);

              // If the user is already logged in, we need tell AASP to switch the login to the new site.
              // If there is an error switching sites, such as the user doesn't have access to the site, log the error and log the user out
              // then continue with the login process

              if(this.authService.getUserLogin()) {
                ConsoleLogger.debug('xxxx - LoginComponent[22].ngOnInit(): getUserLogin exists');
                this.authService.switchSiteLogin(param_siteGuid)
                  .pipe(
                    catchError((error) => {
                      ConsoleLogger.debug('xxxx - LoginComponent[23].ngOnInit(): switchSiteLogin error: ' + JSON.stringify(error));
                      return this.authService.logout('siteSwitchError_mismatched')
                        .pipe(
                          switchMap(() => {
                            ConsoleLogger.debug('xxxx LoginComponent[24]  - siteswitch error. logged out');
                            this.authService.setUserLogin(undefined); // OK to immediately destroy all existing embedded iframes
                            const loginAction: LoginAction = {
                              loginPrompt: LoginPrompt.NONE,
                              fullscreen: true,
                              app: param_app
                            };
                            if (param_nav) {
                              ConsoleLogger.debug('xxxx - LoginComponent[25].ngOnInit(): param_nav exists');
                              loginAction.continuationState = AuthService.CONTINUATION_STATE_NAV_TOKEN + AuthService.stripNavToken(param_nav);
                            }
                            this.authService.login(loginAction, 'siteSwitchError_mismatched');
                            return of();
                          }),
                          takeUntil(this.ngUnsubscribe)
                        );
                    }),
                    takeUntil(this.ngUnsubscribe)
                  ).subscribe(() => {
                    ConsoleLogger.debug('xxxx LoginComponent[26].ngOnInit(): siteswitch complete.');
                    this.processParametersAndRoute(params, unauthenticatedAtDistrict, districtAppId, initialNavLoginFailed);
                });
                return false;
              }
            } else if (!param_siteGuid) {
              ConsoleLogger.debug('xxxx - LoginComponent[27].ngOnInit(): !param_siteGuid branch');
              if(!this.authService.getUserLogin()){
                ConsoleLogger.debug('xxxx - LoginComponent[28].ngOnInit(): !getUserLogin branch');
                resetUserLoginModel = true;
                logoutDebugParam = logoutDebugParam ? logoutDebugParam + '_noSiteGuid' : 'noSiteGuid';
                unauthenticatedAtDistrict = true;
                this.authService.setSiteGuid(undefined);
              }
            }

            if (resetUserLoginModel) {
              ConsoleLogger.debug('xxxx - LoginComponent[29].ngOnInit(): resetUserLoginModel branch');
              this.authService.setUserLogin(undefined); // OK to immediately destroy all existing embedded iframes
              this.authService.logout(logoutDebugParam)
                .pipe(
                  catchError(() => of(undefined)),
                  takeUntil(this.ngUnsubscribe)
                )
                .subscribe();
            }
          }

          ConsoleLogger.debug('xxxx LoginComponent[30].ngOnInit(): - call processParametersAndRoute()');
          this.processParametersAndRoute(params, unauthenticatedAtDistrict, districtAppId, initialNavLoginFailed);

        }
        return false;
      });
  }

  private processParametersAndRoute(params: any, unauthenticatedAtDistrict: boolean, districtAppId: string, initialNavLoginFailed: boolean) {
    ConsoleLogger.debug('xxxx - LoginComponent[31].processParametersAndRoute():  params: ' + JSON.stringify(params));

    let param_app = params['app'];
    let param_nav = params['nav'];
    let param_siteGuid = params['siteGuid'];
    const param_fss_reason = params['fss_reason'];
    const param_templateFamily = params['templateFamily'];
    const param_code = params['code'];
    const param_authCode = params['authCode'];
    const param_ssoChecked = params['ssoChecked'];
    const param_skipGuestLoginPrompt = params['skipGuestLoginPrompt'];

    if( param_templateFamily) {
      ConsoleLogger.debug('xxxx - LoginComponent[32].processParametersAndRoute(): param_templateFamily branch');
      param_app = this.getAppFromAASPFamily(param_templateFamily);
    }

    // get the 'nav' param. If it doesn't exist, check to see if nav info has been set on the continuationstate.
    if (!param_nav) {
      ConsoleLogger.debug('xxxx - LoginComponent[33].processParametersAndRoute(): !param_nav branch');
      if (this.authService.temporary_continuation_state?.indexOf(AuthService.CONTINUATION_STATE_NAV_TOKEN) === 0) {
        param_nav = AuthService.stripNavToken(this.authService.temporary_continuation_state);
        ConsoleLogger.debug('xxxx - LoginComponent[34].processParametersAndRoute(): paramNav updated: ' + JSON.stringify(param_nav));
      }
    }

    if (this.authService.isLoggedIn() && !param_code && !param_authCode) {
      ConsoleLogger.debug('xxxx - LoginComponent[35].processParametersAndRoute(): logged in, not param_code or param_authCode branch');
      if (!param_siteGuid && this.authService.getUserLogin()?.getSiteGuid()) {
        param_nav = undefined;
        ConsoleLogger.debug('xxxx - LoginComponent[36].processParametersAndRoute(): paramNav set to undefined');
      }
      this.initStateService.loginComponentInitialized = true;
      return this.router.navigate(['portal'], { queryParams: { app: param_app, appId: districtAppId, siteGuid: param_siteGuid, nav: param_nav  } });
    } else if(districtAppId && !param_siteGuid) {
      ConsoleLogger.debug('xxxx - LoginComponent[37].processParametersAndRoute(): districtAppId && !param_siteGuid');
      unauthenticatedAtDistrict = true;
    }

    if (param_code || param_authCode) {
      ConsoleLogger.debug('xxxx - LoginComponent[38].processParametersAndRoute(): param_code || param_authCode');
      const code = param_code || param_authCode;

      // Check if this code has already been processed to prevent duplicate redemption
      if (this.processedCode === code) {
        ConsoleLogger.debug('xxxx - LoginComponent[38a].processParametersAndRoute(): code already processed, skipping redemption');
        return false;
      }

      // Mark this code as processed and immediately remove code parameters from URL
      this.processedCode = code;
      ConsoleLogger.debug('xxxx - LoginComponent[38b].processParametersAndRoute(): marking code as processed and cleaning up URL');

      // Redeem logic *MUST* come before the 'ssoChecked' logic since the code always comes back with ssoChecked = true
      this.authService.redeemToken(code)
        .pipe(
          filter(u => !!u),
          take(1),
          takeUntil(this.ngUnsubscribe)
        )
        .subscribe((ulm: UserLoginModel) => {
          ConsoleLogger.debug('xxxx - LoginComponent[39].processParametersAndRoute(): redeemToken subscribe - successful redemption');
          param_siteGuid = ulm?.getSiteGuid();
          if (param_fss_reason) {
            ConsoleLogger.debug('xxxx - LoginComponent[40].processParametersAndRoute(): param_fss_reason branch. reason: ' + param_fss_reason);
            // If the user chose forgot_password or create_account, send that reason to Portal Component
            // to load DBO so the reset functionality is triggered.
            this.initStateService.destinyUserResetRequestReason = param_fss_reason;
            this.router.navigate(['portal'], { queryParams: { app: param_app }});
          } else {
            ConsoleLogger.debug('xxxx - LoginComponent[41].processParametersAndRoute(): !param_fss_reason branch');

            if (!param_siteGuid) {
              ConsoleLogger.debug('xxxx - LoginComponent[42].processParametersAndRoute(): !param_siteGuid branch');
              if (ulm?.getSiteGuid()) {
                ConsoleLogger.debug('xxxx - LoginComponent[43].processParametersAndRoute(): ulm.getSiteGuid() branch');
                param_nav = undefined;
              }
            }

            if (initialNavLoginFailed && ulm?.isGuestUser()) {
              ConsoleLogger.debug('xxxx - LoginComponent[44].processParametersAndRoute(): initialNavLoginFailed && ulm.isGuestUser()');
              this.iframeService.setInitialNavInfoForIframeFromBookmarkUrl(undefined);
              param_nav = undefined;
            } else {
              ConsoleLogger.debug('xxxx - LoginComponent[45].processParametersAndRoute(): !(initialNavLoginFailed && ulm.isGuestUser())');
              this.iframeService.setInitialNavInfoForIframeFromBookmarkUrl({app: param_app, nav: param_nav});
            }

            this.initStateService.skipGuestLoginPrompt = param_skipGuestLoginPrompt === 'true';
            this.initStateService.loginComponentInitialized = true;
            // Navigation to portal with clean queryParams (code/authCode automatically excluded)
            this.router.navigate(['portal'], { queryParams: { app: param_app, appId: districtAppId, siteGuid: param_siteGuid, nav: param_nav } });
          }
        });
    } else if (districtAppId) {
      ConsoleLogger.debug('xxxx - LoginComponent[46].processParametersAndRoute(): districtAppId branch');
      if (param_ssoChecked === 'true') {
        ConsoleLogger.debug('xxxx - LoginComponent[47].processParametersAndRoute(): param_ssoChecked === true');
        this.authService.logout('ssoChecked')
          .pipe(take(1), takeUntil(this.ngUnsubscribe))
          .subscribe(() => this.authService.login({
            loginPrompt: LoginPrompt.LOGIN,
            fullscreen: true,
            app: param_app
          }, 'ssoChecked')
        );
      } else if (unauthenticatedAtDistrict) {
        ConsoleLogger.debug('xxxx - LoginComponent[48].processParametersAndRoute(): unauthenticatedAtDistrict branch');
        this.initStateService.loginComponentInitialized = true;
        this.iframeService.setInitialNavInfoForIframeFromBookmarkUrl({app: param_app, nav: param_nav});
        return this.router.navigate(['portal'], { queryParams: { app: param_app, appId: districtAppId, nav: param_nav } });
      } else {
        ConsoleLogger.debug('xxxx - LoginComponent[49].processParametersAndRoute(): else branch');

        const loginAction: LoginAction = {
          loginPrompt: LoginPrompt.NONE,
          fullscreen: true,
          app: param_app
        };

        if (param_app && param_nav) {
          ConsoleLogger.debug('xxxx - LoginComponent[50].processParametersAndRoute(): param_app && param_nav');
          this.iframeService.setInitialNavInfoForIframeFromBookmarkUrl({app: param_app, nav: param_nav});
          loginAction.continuationState = AuthService.CONTINUATION_STATE_NAV_TOKEN + AuthService.stripNavToken(param_nav);
        }

        ConsoleLogger.debug('xxxx LoginComponent[51] processParametersAndRoute5a logging in with: ' + JSON.stringify(loginAction));
        this.authService.login(loginAction, 'distIdOnly');
      }
    } else {
      ConsoleLogger.debug('xxxx - LoginComponent[52].processParametersAndRoute(): final else branch');
      this.authService.login({
        loginPrompt: LoginPrompt.LOGIN,
        fullscreen: true,
        app: param_app
      }, 'noDistId');
    }
    return false;
  }

  private getAppFromAASPFamily(aaspFamily: string) : Application {
    let app: Application = null;
    switch(aaspFamily) {
      case AaspTemplateFamily.DD:
        app = Application.DD;
        break;
      case AaspTemplateFamily.DESTINY:
        app = Application.DESTINY;
        break;
      case AaspTemplateFamily.DASHBOARD:
        app = Application.DASHBOARD;
        break;
      case AaspTemplateFamily.COLLECTIONS:
        app = Application.COLLECTIONS;
        break;
      case AaspTemplateFamily.CLM:
        app = Application.CLM;
        break;
      default:
        break;
    }
    ConsoleLogger.debug('xxxx - LoginComponent[53].getAppFromAASPFamily(): called with: ' + aaspFamily + ', returning: ' + app);
    return app;
  }


}
